import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import {
  useGetDefinitionDescription,
  useGetInvDefinitionDescription,
  useUpdateArticleDescription,
  useUpdateDefinitionsDescription,
  useUpdateInvArticleDescription,
} from "../../queries/investorPortal";
import { useAuthStore } from "../../store";
import { useRightsStore } from "../../store/rightsStore";
import BCHeader from "../../shared/BCHeader";
import { Box, HStack } from "../../components/utils";
import Loader from "../../shared/Loader";
import { EmptyTable } from "../../components/shared/EmptyTable";
import { useGetCompanyDetails } from "../../queries/termsheet";
import { queryClient } from "../../queries";
import { DefinitionModel } from "../../types/ShareHolderRightsModel";
import Tooltip from "../../components/shared/Tooltip";
import {
  ButtonSize,
  IconCTAButton,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";

export const RightsDefinitions = () => {
  const { cin, id, token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<DefinitionModel[]>([]);
  const companyId = useAuthStore.getState().companyId || "";
  const { data: nodeCompanyDetail } = useGetCompanyDetails();
  const cinNumber = nodeCompanyDetail?.companyDetails.cinNumber || "";
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showFilePopup, setSHowFilePopup] = useState<boolean>(false);
  const [file, setFile] = useState<File>();
  const featureHidden = false;
  const rightsId = useRightsStore.getState().rightsId || id;

  const handleDoubleClick = (index: number) => {
    handleExpandRow(index);
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        isEditable: index === idx ? !row.isEditable : row.isEditable,
      }))
    );
  };

  const { data: definitionData, isFetched } = cin
    ? useGetInvDefinitionDescription(rightsId, companyId, cin)
    : useGetDefinitionDescription(rightsId, companyId, cinNumber);

  useEffect(() => {
    if (definitionData?.definitions) {
      const sortedArray = sortArticle(definitionData?.definitions);
      setData(sortedArray);
    }
  }, [definitionData, isFetched]);

  const sortArticle = (article: DefinitionModel[]) => {
    const sortedArray = article.sort((a, b) => {
      const parts1 = a.name.split(".").map(Number);
      const parts2 = b.name.split(".").map(Number);
      for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
        const part1 = parts1[i] || 0;
        const part2 = parts2[i] || 0;

        if (part1 < part2) return -1;
        if (part1 > part2) return 1;
      }
      return 0;
    });
    return sortedArray;
  };

  const { mutate: updateDefinitionDescription } =
    useUpdateDefinitionsDescription();

  const handleExpandRow = (index: number) => {
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        isExpanded: index === idx ? !row.isExpanded : row.isExpanded,
        isEditable: index === idx ? row.isEditable : false,
      }))
    );
  };

  const handleInputArticleNumberChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        articleNumber: index === idx ? value : row.name.trim(),
        isEditable: index === idx,
        isExpanded: index === idx,
      }))
    );
  };

  const handleAddEntry = () => {
    setData((prevState) => [
      ...prevState,
      {
        id: 0,
        name: "",
        description: "",
        isEditable: true,
      },
    ]);
  };
  const handleDelete = (index: number) => {
    setData((prevState) => prevState.filter((_, idx) => idx !== index));
  };

  const handleInputArticleDescriptionChange = (
    index: number,
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        description: index === idx ? value : row.description,
        isEditable: index === idx,
        isExpanded: index === idx,
      }))
    );
  };

  const handleSubmit = () => {
    let error = "";
    data.forEach((element) => {
      if (!element.name) error = "Definitions name cant be Empty";
    });
    if (error === "")
      updateDefinitionDescription(
        {
          definitions: data as DefinitionModel[],
          rightsId: rightsId!,
        },
        {
          onSuccess: () => {
            toast("Definitions Updated Successfully!", {
              type: "success",
              autoClose: 2000,
            });
            queryClient.invalidateQueries("getInvArticleDescription");
            queryClient.invalidateQueries("getArticleDescription");
          },
          onError: () => {
            toast("Something went wrong", { type: "error", autoClose: 2000 });
          },
        }
      );
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = data.filter(
    (row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      {!cin && (
        <BCHeader
          className="items-baseline mb-2"
          bcTitle="Rights Shareholder Mapping"
          bcSubTitle="Definitions"
        />
      )}

      {!definitionData ? (
        <Box className="my-auto bg-white rounded-lg h-full w-full shadow-box">
          <Loader />
        </Box>
      ) : (
        <div className="w-full p-6 bg-white">
          <div className="flex justify-between items-center mb-4 ">
            <div className="text-xl font-semibold text-[#464E5F]">
              Definitions
            </div>
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Search..."
                className="p-2 border border-gray-300 h-9 bg-slate-50 rounded-md"
                onChange={handleSearch}
              />
              {!cin && (
                <button
                  className="border-orange-501 text-orange-501 border font-semibold py-1.5 px-3 rounded"
                  data-event-name="Back From Definitions"
                  onClick={() => {
                    navigate("/rights/shareholder");
                  }}
                >
                  Back
                </button>
              )}
              <button
                className=" bg-orange-501 text-white font-semibold py-2 px-4 rounded"
                onClick={handleSubmit}
              >
                Save
              </button>
            </div>
          </div>
          <div className="flex flex-col justify-between items-center mb-4">
            <table className="w-full table-fixed rounded-lg border-separate border-spacing-0 bg-white">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-2 w-60 border font-semibold border-gray-300 text-[#464E5F]">
                    Definitions Name
                  </th>
                  <th className="p-2 flex-1 border font-semibold border-gray-300 text-[#464E5F]">
                    Definitions Description
                  </th>
                  <th className="p-2 w-16 border border-gray-300"></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => (
                  <>
                    <tr
                      key={row.id}
                      className={
                        index % 2
                          ? "bg-slate-50 hover:bg-slate-100"
                          : "bg-inherit  hover:bg-slate-100"
                      }
                    >
                      <td
                        className={`p-2 w-32 border border-gray-300 font-semibold ${
                          row.isEditable ? "bg-white" : "cursor-pointer"
                        }`}
                        onClick={() => handleExpandRow(index)}
                      >
                        {/* {row.isEditable ? (
                          <input
                            type="text"
                            name="articleNumber"
                            value={row.articleNumber}
                            onChange={(event) =>
                              handleInputArticleNumberChange(index, event)
                            }
                            className="w-full"
                          />
                        ) : (
                          row.articleNumber
                        )} */}
                        {row.name}
                      </td>
                      <td
                        className={`p-2 flex-1 border border-gray-300 ${
                          row.isEditable ? "bg-white" : "cursor-pointer"
                        }`}
                      >
                        {row.isEditable ? (
                          <textarea
                            name="articleDescription"
                            value={row.description}
                            onChange={(event) =>
                              handleInputArticleDescriptionChange(index, event)
                            }
                            rows={4}
                            className="w-full resize-y border text-sm border-gray-300"
                          />
                        ) : (
                          <div
                            onClick={() => handleExpandRow(index)}
                            className="w-full break-words h-6 overflow-clip"
                          >
                            {row.description}
                          </div>
                        )}
                      </td>
                      <td className="w-16  border border-gray-300">
                        <HStack className="justify-evenly">
                          {row.isEditable ? (
                            <Icon
                              icon="mdi:tick"
                              className="hover:cursor-pointer text-orange-501 scale-125 rounded"
                              height={18}
                              width={18}
                              onClick={() => handleDoubleClick(index)}
                            />
                          ) : (
                            <Icon
                              icon="mi:edit"
                              className="hover:cursor-pointer text-orange-501 scale-125 rounded"
                              height={18}
                              width={18}
                              onClick={() => handleDoubleClick(index)}
                            />
                          )}
                          {featureHidden && (
                            <Icon
                              icon="mi:delete"
                              onClick={() => handleDelete(index)}
                              className={`hover:cursor-pointer text-orange-501 scale-125 rounded `}
                              height={18}
                              width={18}
                            />
                          )}
                        </HStack>
                      </td>
                    </tr>

                    {row.isExpanded && (
                      <tr>
                        <td
                          colSpan={3}
                          className={`p-2 flex-1  border border-gray-300`}
                        >
                          <div className="w-full break-words">
                            {row.description}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
            {filteredData.length === 0 && (
              <EmptyTable header="No Definitions Found" subHeader="" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
